<template>
  <!-- Create / edit a Customer Aftercare record (Installed System / Device
       Purchase / Repair) with its components. Saving a new record starts the
       schedule server-side. -->
  <v-dialog v-model="show" width="820" persistent scrollable>
    <v-card>
      <ModalHeader
        :title="isEdit ? 'Edit Aftercare Record' : 'New Aftercare Record'"
        icon="mdi-face-agent"
        :subtitle="isEdit ? form.system_type : 'Installed system, device purchase or repair'"
        @close="close"
      />
      <v-card-text class="pa-4">
        <v-form ref="form">
          <div class="section-label">
            <v-icon x-small>mdi-account</v-icon> Customer &amp; Item
          </div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-autocomplete
                v-model="form.customer_id"
                :items="customerItems"
                label="Customer"
                :rules="[(v) => !!v || 'Required']"
                outlined
                dense
                @change="onCustomerChange"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.branch_id"
                :items="branchItems"
                label="Branch (owns the account)"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.category"
                :items="categoryItems"
                label="Category"
                :rules="[(v) => !!v || 'Required']"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-combobox
                v-model="form.system_type"
                :items="subtypeItems"
                label="Type"
                :rules="[(v) => !!v || 'Required']"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.site_municipality"
                label="Site Municipality (used for batching)"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="form.site_address"
                label="Site Address"
                hint="The actual site — may differ from the customer address"
                persistent-hint
                :rules="[(v) => !!v || 'Required']"
                outlined
                dense
              />
            </v-col>
          </v-row>

          <div class="section-label mt-3">
            <v-icon x-small>mdi-calendar-wrench</v-icon> Installation &amp; Maintenance
          </div>
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.installed_on"
                :label="dateLabel"
                type="date"
                :rules="[(v) => !!v || 'Required']"
                outlined
                dense
                class="invoice-date-input"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model.number="form.maintenance_cadence_months"
                :label="isRepair ? 'Follow-up after (months)' : 'Every (months)'"
                type="number"
                min="1"
                max="24"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="12" sm="4" v-if="!isRepair">
              <v-text-field
                v-model.number="form.max_visits"
                label="Number of visits (end)"
                type="number"
                min="1"
                max="24"
                hint="Schedule ends after this many visits"
                persistent-hint
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-select
                v-model="form.visit_mode"
                :items="modeItems"
                label="Mode"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.free_until"
                label="Free Until"
                type="date"
                hint="Defaults to installed + 12 months"
                persistent-hint
                outlined
                dense
                class="invoice-date-input"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.warranty_end"
                label="Warranty End"
                type="date"
                outlined
                dense
                hide-details
                class="invoice-date-input"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-select
                v-model="form.status"
                :items="statuses"
                label="Status"
                outlined
                dense
                hide-details
              />
            </v-col>
          </v-row>
          <v-alert dense text type="info" class="mt-2 mb-0">
            {{ modeHint }}
          </v-alert>

          <div class="section-label mt-4 d-flex align-center">
            <span><v-icon x-small>mdi-chip</v-icon> Components</span>
            <v-spacer />
            <v-btn x-small color="primary" depressed @click="addComponent">
              <v-icon left x-small>mdi-plus</v-icon> Add Part
            </v-btn>
          </div>
          <v-simple-table dense class="bordered-table rounded-lg">
            <thead>
              <tr>
                <th>Description</th>
                <th style="width: 90px">Qty</th>
                <th style="width: 180px">Serial No.</th>
                <th style="width: 48px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(c, i) in form.components" :key="i">
                <td>
                  <v-text-field
                    v-model="c.description"
                    placeholder="e.g. 4MP bullet camera"
                    dense
                    hide-details
                    flat
                    solo
                  />
                </td>
                <td>
                  <v-text-field
                    v-model.number="c.quantity"
                    type="number"
                    min="1"
                    dense
                    hide-details
                    flat
                    solo
                  />
                </td>
                <td>
                  <v-text-field
                    v-model="c.serial_number"
                    dense
                    hide-details
                    flat
                    solo
                  />
                </td>
                <td>
                  <v-btn icon small color="error" @click="removeComponent(i)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
              <tr v-if="!form.components.length">
                <td colspan="4" class="text-center grey--text py-3">
                  No components added
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <div class="section-label mt-4">
            <v-icon x-small>mdi-note-text-outline</v-icon> Notes
          </div>
          <v-textarea
            v-model="form.notes"
            rows="2"
            outlined
            dense
            hide-details
            auto-grow
          />
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions class="px-4 py-3">
        <span class="caption grey--text">installedSystemFormModal.vue</span>
        <v-spacer />
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn color="primary" depressed :loading="saving" @click="save">
          {{ isEdit ? "Save Changes" : "Create Record" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  name: "InstalledSystemFormModal",
  props: {
    value: { type: Boolean, default: false },
    editing: { type: Object, default: null },
  },
  data() {
    return {
      saving: false,
      modeItems: [
        { text: "Onsite", value: "onsite" },
        { text: "Call (remote)", value: "remote" },
      ],
      categoryItems: [
        { text: "Installed System", value: "installed_system" },
        { text: "Device Purchase", value: "device_purchase" },
        { text: "Repair", value: "repair" },
      ],
      subtypesByCategory: {
        installed_system: ["CCTV", "Starlink", "Solar", "Network", "Server"],
        device_purchase: ["Laptop", "Desktop", "Printer"],
        repair: ["Laptop", "Desktop", "Printer", "CCTV", "Other"],
      },
      statuses: [
        { text: "Active", value: "active" },
        { text: "Dormant", value: "dormant" },
        { text: "Ended", value: "ended" },
      ],
      form: this.blankForm(),
    };
  },
  computed: {
    ...mapGetters({
      customers: "customer/customers",
      branches: "branch/branches",
    }),
    show: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    isEdit() {
      return !!(this.editing && this.editing.id);
    },
    customerItems() {
      return this.customers.map((c) => ({
        text: c.customer_name,
        value: c.id,
      }));
    },
    branchItems() {
      return this.branches.map((b) => ({
        text: b.branch_name,
        value: b.id,
      }));
    },
    isRepair() {
      return this.form.category === "repair";
    },
    subtypeItems() {
      return this.subtypesByCategory[this.form.category] || [];
    },
    dateLabel() {
      return this.form.category === "installed_system"
        ? "Completion / Turn-over date"
        : "Delivery / Pickup date";
    },
    modeHint() {
      const n = this.form.max_visits || 0;
      if (this.isRepair) {
        return this.form.visit_mode === "onsite"
          ? "One-time on-site follow-up (lands on the Field Order calendar)."
          : "One-time follow-up call (phone/online, off the calendar).";
      }
      if (this.form.visit_mode === "onsite") {
        return `All ${n} visits will be scheduled on site (each lands on the Field Order calendar).`;
      }
      return `All ${n} visits are remote calls (phone/online) — none appear on the Field Order calendar.`;
    },
  },
  watch: {
    value(v) {
      if (v) this.load();
    },
  },
  created() {
    if (!this.customers.length) this.get_customers();
    if (!this.branches.length) this.get_branches();
  },
  methods: {
    ...mapActions({
      get_customers: "customer/get_customers",
      get_branches: "branch/get_branches",
      create_system: "installed_system/create_system",
      update_system: "installed_system/update_system",
    }),
    blankForm() {
      return {
        customer_id: null,
        branch_id: null,
        invoice_id: null,
        category: "installed_system",
        system_type: null,
        site_address: "",
        site_municipality: "",
        installed_on: new Date().toISOString().substr(0, 10),
        maintenance_cadence_months: 3,
        max_visits: 4,
        visit_mode: "onsite",
        free_until: null,
        warranty_end: null,
        status: "active",
        notes: "",
        components: [],
      };
    },
    load() {
      if (this.isEdit) {
        const e = this.editing;
        this.form = {
          customer_id: e.customer_id,
          branch_id: e.branch_id,
          invoice_id: e.invoice_id,
          category: e.category || "installed_system",
          system_type: e.system_type,
          site_address: e.site_address,
          site_municipality: e.site_municipality,
          installed_on: this.dateOnly(e.installed_on),
          maintenance_cadence_months: e.maintenance_cadence_months || 3,
          max_visits: e.max_visits || 4,
          // Simplify legacy "alternating" to onsite; else derive from remote flag.
          visit_mode:
            e.visit_mode === "remote"
              ? "remote"
              : e.visit_mode === "onsite"
              ? "onsite"
              : e.has_remote_access
              ? "remote"
              : "onsite",
          free_until: this.dateOnly(e.free_until),
          warranty_end: this.dateOnly(e.warranty_end),
          status: e.status || "active",
          notes: e.notes || "",
          components: (e.components || []).map((c) => ({
            item_id: c.item_id,
            description: c.description,
            quantity: c.quantity || 1,
            serial_number: c.serial_number,
          })),
        };
      } else {
        this.form = this.blankForm();
      }
    },
    dateOnly(d) {
      return d ? String(d).substr(0, 10) : null;
    },
    onCustomerChange(id) {
      // Default site municipality from the picked customer (editable).
      if (this.form.site_municipality) return;
      const c = this.customers.find((x) => x.id === id);
      if (c && c.customer_municipality) {
        this.form.site_municipality = c.customer_municipality;
      }
    },
    addComponent() {
      this.form.components.push({
        item_id: null,
        description: "",
        quantity: 1,
        serial_number: null,
      });
    },
    removeComponent(i) {
      this.form.components.splice(i, 1);
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      try {
        const payload = {
          ...this.form,
          components: this.form.components.filter(
            (c) => c.description && c.description.trim()
          ),
        };
        // Repairs are a single one-time follow-up.
        if (this.isRepair) payload.max_visits = 1;
        if (!payload.free_until) delete payload.free_until;
        if (!payload.warranty_end) delete payload.warranty_end;
        if (this.isEdit) {
          await this.update_system({ id: this.editing.id, payload });
          this.$alert({
            title: "Saved",
            message: "Aftercare record updated.",
            color: "success",
            icon: "mdi-check-circle",
          });
        } else {
          await this.create_system(payload);
          this.$alert({
            title: "Created",
            message: "Aftercare record created and schedule started.",
            color: "success",
            icon: "mdi-check-circle",
          });
        }
        this.$emit("saved");
        this.close();
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            (e.response && e.response.data && e.response.data.error) ||
            "Failed to save installed system.",
          color: "error",
          icon: "mdi-alert",
        });
      } finally {
        this.saving = false;
      }
    },
    close() {
      this.show = false;
    },
  },
};
</script>
